import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, EventEmitter, Input, Output, Type } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { CurrencyModel, IncoTermModel, ValidationMessageModel } from '@data-layer/models/common';
import { enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { DateHelper } from '@helpers/date.helper';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { EsEntityUnifyDefaultDesktopComponent, EsInvoiceFormDesktopComponent } from '@shared/es-mutual/components';
import { EntityModel } from '@data-layer/models/entities';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { enumWizardComponent } from '@enum/enumWizardComponent';

@Component({
  selector: 'es-invoice-stepper',
  templateUrl: './es-invoice-stepper.component.html',
  styleUrls: ['./es-invoice-stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsInvoiceStepperComponent {
  @Input() public componentName: enumWizardComponent;
  @Input() public componentRef: Type<any>;
  @Input() public currentWizardIndex: number;
  @Input() public totalWizardIndexes: number;
  @Input() public title: string = 'INVOICE_REVIEW_FIX';
  @Input() public backTitle: string = 'BUTTON.BACK';
  @Input() public nextTitle: string = 'LABELS.NEXT';

  /* below are inputs from inner components in one place */
  /* EsInvoiceFormDesktopComponent */
  @Input() public invoiceFg: UntypedFormGroup;
  @Input() public currencies: CurrencyModel[] = [];
  @Input() public transportationMode: enumTransportationMode;
  @Input() public entrySummaryId: number;
  @Input() public incoterms: IncoTermModel[] = [];
  @Input() public index: number;
  @Input() public invoice: InvoiceModel;
  @Input() public exportDate: string = DateHelper.now;
  @Input() public currencyRate: number;
  @Input() public allValidationMessages: ValidationMessageModel[] = [];
  @Input() public isHideFooter: boolean;

  /* EsInvoiceFormDesktopComponent */
  @Input() public formGroup: UntypedFormGroup;
  @Input() public radioButton: AbstractControl;
  @Input() public info: {
    code: enumEntityModelCode;
    CODE: enumEntityModelCode;
    imgSrc: string;
    titleDesktop: string;
    subTitleDesktop: string;
    hint: string;
    addDetails: string;
    tooltip: string;
    addNew: string;
    subTitleHint: string;
  } = null;
  @Input() public entityCode: enumEntityModelCode;
  @Input() public entityKeyName: string;
  @Input() public inheritedEntity: EntityModel;
  @Input() public entity: EntityModel;

  /* EsLineItemFormDesktopComponent */
  @Input() public itemId: number;
  @Input() public shipmentId: number;
  @Input() public isShowHint: boolean;
  @Input() public validateImmediately: boolean;

  /* EsInvoiceFormDesktopComponent */
  @Output() public invoiceDataSaved: EventEmitter<Partial<InvoiceModel>> = new EventEmitter();
  /* EsInvoiceFormDesktopComponent */
  @Output() public entityDataSaved: EventEmitter<EntityModel> = new EventEmitter();
  /* EsLineItemFormDesktopComponent */
  @Output() public lineDataSaved: EventEmitter<LineModel> = new EventEmitter();
  /** #13383 F6: the line form persisted a new line for the assistant — re-bind its id so the directive keeps it. */
  @Output() public lineCreated: EventEmitter<number> = new EventEmitter();

  private innerComponentRef: ComponentRef<any>;

  constructor(private entrySummaryStateService: EntrySummaryStateService, private cdr: ChangeDetectorRef) {}

  public setInnerComponentRef(ref: ComponentRef<any>): void {
    this.innerComponentRef = ref;
  }

  public next(): void {
    this.innerComponentRef.instance.submit();
  }

  public back(): void {
    this.entrySummaryStateService.wizardStepPrev$.next();
  }

  public handleInvoiceDataSaved($event: Partial<InvoiceModel>): void {
    this.invoiceDataSaved.emit($event);
  }

  public handleEntityDataSaved($event: EntityModel): void {
    this.entityDataSaved.emit($event);
  }

  public handleLineDataSaved($event: LineModel): void {
    this.lineDataSaved.emit($event);
  }

  public handleLineCreated(id: number): void {
    // `[dynamic-component]` re-assigns every input of the form on each check: the id must come from here, not the form
    this.itemId = id;
    this.lineCreated.emit(id);
    this.cdr.markForCheck();
  }

  public runDetection(): void {
    this.cdr.detectChanges();
  }

  public get componentInputs() {
    switch (this.componentName) {
      case enumWizardComponent.invoiceFormDesktopComponent:
        return this.invoiceDetailsInputs;
      case enumWizardComponent.entityUnifyDefaultDesktopComponent:
        return this.invoiceMFInputs;
      case enumWizardComponent.lineItemFormDesktopComponent:
        return this.invoiceLineInputs;
    }
  }

  public get componentOutputs() {
    switch (this.componentName) {
      case enumWizardComponent.invoiceFormDesktopComponent:
        return this.invoiceDetailsOutputs;
      case enumWizardComponent.entityUnifyDefaultDesktopComponent:
        return this.invoiceMFOutputs;
      case enumWizardComponent.lineItemFormDesktopComponent:
        return this.invoiceLineOutputs;
    }
  }

  public get invoiceDetailsInputs() {
    return {
      invoiceFg: this.invoiceFg,
      currencies: this.currencies,
      transportationMode: this.transportationMode,
      entrySummaryId: this.entrySummaryId,
      incoterms: this.incoterms,
      index: this.index,
      invoice: this.invoice,
      exportDate: this.exportDate,
      currencyRate: this.currencyRate,
      allValidationMessages: this.allValidationMessages,
      isHideFooter: true,
      isShowClear: true,
    };
  }

  public get invoiceMFInputs() {
    return {
      formGroup: this.formGroup,
      radioButton: this.radioButton,
      info: this.info,
      entityCode: this.entityCode,
      entityKeyName: this.entityKeyName,
      inheritedEntity: this.inheritedEntity,
      entity: this.entity,
      isHideFooter: true,
    };
  }

  public get invoiceLineInputs() {
    return {
      itemId: this.itemId,
      shipmentId: this.shipmentId,
      invoice: this.invoice,
      isShowHint: this.isShowHint,
      validateImmediately: this.validateImmediately,
      isHideFooter: true,
    };
  }

  public get invoiceDetailsOutputs() {
    return { invoiceDataSaved: this.handleInvoiceDataSaved.bind(this) };
  }

  public get invoiceMFOutputs() {
    return { entityDataSaved: this.handleEntityDataSaved.bind(this) };
  }

  public get invoiceLineOutputs() {
    return { lineDataSaved: this.handleLineDataSaved.bind(this), lineCreated: this.handleLineCreated.bind(this) };
  }

  public get barWidth(): number {
    return Math.round(((this.currentWizardIndex + 1) / this.totalWizardIndexes) * 100);
  }
}
